import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import Nav from '../../components/Nav';
import Footer from '../../components/Footer';
import { useAuth } from '../../context/AuthContext';
import { 
  createCertificatePaymentSession, 
  verifyCertificatePayment
} from '../../services/api';
import { ShieldCheck, Truck, CreditCard, ArrowLeft, RefreshCw } from 'lucide-react';

const CertificateApplication = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const courseId = searchParams.get('courseId') || '1';
  const courseTitle = searchParams.get('title') || 'Electric vehicle charging operation and maintenance';
  const price = searchParams.get('price') || '1';

  const returnOrderId = searchParams.get('order_id') || searchParams.get('orderId');

  const [formData, setFormData] = useState({
    fullName: user?.name || '',
    fatherName: '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [submittedOrder, setSubmittedOrder] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  // Dynamically load Cashfree JS SDK v3
  const loadCashfreeSdk = () => {
    return new Promise((resolve) => {
      if (window.Cashfree) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  useEffect(() => {
    loadCashfreeSdk();
  }, []);

  // Pre-fill User Info if logged in
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        fullName: prev.fullName || user.name || '',
        email: prev.email || user.email || ''
      }));
    }
  }, [user]);

  // Check if returning from Cashfree Payment Redirect (Query Param ?order_id=...)
  useEffect(() => {
    if (returnOrderId) {
      setVerifying(true);
      verifyCertificatePayment({ order_id: returnOrderId })
        .then(res => {
          if (res.success && res.order) {
            setSubmittedOrder(res.order);
          } else {
            setErrorMsg(res.message || 'Payment verification failed. Please contact support if debited.');
          }
        })
        .catch(err => {
          console.error("Payment verification error:", err);
          setErrorMsg('Error verifying payment. Please contact support.');
        })
        .finally(() => {
          setVerifying(false);
        });
    }
  }, [returnOrderId]);

  // Handle Form Submission & Cashfree Payment Launch
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.email || !formData.phone || !formData.address || !formData.city || !formData.pincode) {
      setErrorMsg('Please fill in all required candidate and delivery address fields.');
      return;
    }

    setErrorMsg('');
    setLoading(true);

    try {
      // 1. Ensure Cashfree SDK script is loaded
      const isSdkReady = await loadCashfreeSdk();
      if (!isSdkReady) {
        setErrorMsg('Failed to load Cashfree payment gateway script. Please check your internet connection.');
        setLoading(false);
        return;
      }

      // 2. Create Payment Session with Backend
      const payload = {
        courseId,
        courseTitle,
        amount: parseFloat(price),
        ...formData
      };

      const res = await createCertificatePaymentSession(payload);

      if (res.success && res.payment_session_id) {
        // 3. Launch Cashfree PG Checkout Modal / Redirect
        const cfMode = res.cf_environment === 'PROD' ? 'production' : 'sandbox';
        const cashfree = window.Cashfree({ mode: cfMode });
        
        cashfree.checkout({
          paymentSessionId: res.payment_session_id,
          redirectTarget: '_self'
        });
      } else if (res.success && res.order_id) {
        // If Cashfree session failed, verify status
        const verifyRes = await verifyCertificatePayment({ order_id: res.order_id });
        if (verifyRes.success && verifyRes.order) {
          setSubmittedOrder(verifyRes.order);
        } else {
          setErrorMsg(res.message || 'Failed to initialize Cashfree payment session.');
        }
      } else {
        setErrorMsg(res.message || 'Failed to initiate Cashfree payment session.');
      }
    } catch (err) {
      console.error('Certificate application error:', err);
      setErrorMsg(err.message || 'An error occurred while creating your order.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50 flex flex-col pt-14 sm:pt-16 font-sans">
      <Nav />
      <main className="flex-1 max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 w-full space-y-4 sm:space-y-6">

        {/* Back Link */}
        {!submittedOrder && (
          <Link to={`/courseoverview?course_id=${courseId}`} className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-gray-600 hover:text-emerald-600 transition-colors">
            <ArrowLeft size={16} /> Back to Course Overview
          </Link>
        )}

        {/* Verifying Loader */}
        {verifying ? (
          <div className="bg-white p-8 sm:p-12 rounded-3xl border border-gray-100 shadow-xl text-center space-y-4 my-8">
            <RefreshCw size={36} className="text-emerald-600 animate-spin mx-auto" />
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Verifying Cashfree Payment...</h2>
            <p className="text-xs sm:text-sm text-gray-500">Please wait while we confirm your Cashfree payment transaction.</p>
          </div>
        ) : submittedOrder ? (
          /* DYNAMIC ORDER CONFIRMATION CARD (MATCHING USER SCREENSHOT) */
          <div className="bg-white p-6 sm:p-10 rounded-3xl border border-gray-100 shadow-xl text-center space-y-6 max-w-2xl mx-auto animate-in fade-in zoom-in duration-300">
            <div>
              <span className="inline-block px-3.5 py-1 bg-emerald-100 text-emerald-800 text-[10px] sm:text-xs font-black rounded-full uppercase tracking-wider mb-3">
                Payment Received & Application Confirmed
              </span>
              <h2 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight">
                Certificate Order Placed!
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-2 leading-relaxed">
                Thank you, <strong className="text-gray-900">{submittedOrder.fullName}</strong>. Your assessment application for <strong className="text-gray-900">{submittedOrder.courseTitle}</strong> has been registered.
              </p>
            </div>

            {/* Dynamic Receipt Details Table */}
            <div className="bg-gray-50/80 rounded-2xl border border-gray-200/80 p-4 sm:p-6 text-left space-y-3.5 text-xs sm:text-sm">
              <div className="flex justify-between items-center py-1.5 border-b border-gray-200/60">
                <span className="text-gray-500 font-semibold">Order Reference:</span>
                <span className="font-mono font-bold text-emerald-700">{submittedOrder.orderReference}</span>
              </div>

              <div className="flex justify-between items-center py-1.5 border-b border-gray-200/60">
                <span className="text-gray-500 font-semibold">Certificate Type:</span>
                <span className="font-bold text-gray-900">{submittedOrder.certificateType || 'Official Physical Hardcopy'}</span>
              </div>

              <div className="flex justify-between items-center py-1.5 border-b border-gray-200/60">
                <span className="text-gray-500 font-semibold">Amount Paid:</span>
                <span className="font-extrabold text-gray-900 text-base">₹{submittedOrder.amount}</span>
              </div>

              <div className="flex justify-between items-start py-1.5 border-b border-gray-200/60">
                <span className="text-gray-500 font-semibold shrink-0 w-32">Delivery Address:</span>
                <span className="font-medium text-gray-800 text-right leading-relaxed">{submittedOrder.deliveryAddress}</span>
              </div>

              <div className="flex justify-between items-center py-1.5">
                <span className="text-gray-500 font-semibold">Dispatch Status:</span>
                <span className="px-2.5 py-1 bg-amber-100/90 text-amber-900 border border-amber-300/80 text-[10px] sm:text-xs font-bold rounded-lg">
                  {submittedOrder.dispatchStatus || 'Under Assessment Verification (Dispatches in 3-5 days)'}
                </span>
              </div>
            </div>

            <div className="pt-2">
              <button
                onClick={() => navigate('/student/dashboard')}
                className="w-full sm:w-auto px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm rounded-xl shadow-md transition-all cursor-pointer"
              >
                Go to Student Dashboard
              </button>
            </div>
          </div>
        ) : (
          /* CERTIFICATE APPLICATION FORM */
          <form onSubmit={handleSubmit} className="bg-white p-4 sm:p-7 md:p-8 rounded-2xl sm:rounded-3xl border border-gray-100 shadow-sm space-y-5 sm:space-y-6">

            {/* Course Summary Banner */}
            <div className="bg-emerald-50/80 border border-emerald-200 p-3.5 sm:p-5 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="space-y-0.5">
                <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wide">Enrolled Course</p>
                <h3 className="font-bold text-gray-900 text-xs sm:text-base leading-snug">{courseTitle}</h3>
                <p className="text-[11px] text-gray-500">Includes Official Physical Hardcopy Certificate + Assessment Registration</p>
              </div>
              <div className="text-left sm:text-right shrink-0 border-t sm:border-t-0 pt-2 sm:pt-0 border-emerald-200/60 flex sm:block items-center justify-between">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Total Fee</p>
                <p className="text-lg sm:text-2xl font-black text-emerald-700">₹{parseFloat(price || 1).toFixed(2)}</p>
              </div>
            </div>

            {errorMsg && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl text-xs sm:text-sm font-semibold">
                {errorMsg}
              </div>
            )}

            {/* Candidate & Printing Details */}
            <div className="space-y-3 sm:space-y-4">
              <h3 className="text-xs sm:text-base font-bold text-gray-900 border-b border-gray-100 pb-2 flex items-center gap-2">
                <ShieldCheck size={16} className="text-emerald-600 shrink-0" /> Candidate & Printing Information
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-[11px] sm:text-xs font-bold text-gray-700 uppercase mb-1">
                    Full Name (To print on Certificate) *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none text-xs sm:text-sm"
                    placeholder="Admin User"
                  />
                </div>

                <div>
                  <label className="block text-[11px] sm:text-xs font-bold text-gray-700 uppercase mb-1">
                    Father / Guardian Name
                  </label>
                  <input
                    type="text"
                    value={formData.fatherName}
                    onChange={(e) => setFormData({ ...formData, fatherName: e.target.value })}
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none text-xs sm:text-sm"
                    placeholder="Father's full name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-[11px] sm:text-xs font-bold text-gray-700 uppercase mb-1">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none text-xs sm:text-sm"
                    placeholder="admin@gmail.com"
                  />
                </div>

                <div>
                  <label className="block text-[11px] sm:text-xs font-bold text-gray-700 uppercase mb-1">Phone / Mobile Number *</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none text-xs sm:text-sm"
                    placeholder="7605812086"
                  />
                </div>
              </div>
            </div>

            {/* Shipping Address Details */}
            <div className="space-y-3 sm:space-y-4 pt-1">
              <h3 className="text-xs sm:text-base font-bold text-gray-900 border-b border-gray-100 pb-2 flex items-center gap-2">
                <Truck size={16} className="text-emerald-600 shrink-0" /> Physical Delivery Shipping Address
              </h3>

              <div>
                <label className="block text-[11px] sm:text-xs font-bold text-gray-700 uppercase mb-1">House / Flat No, Street, Landmark *</label>
                <textarea
                  rows={2}
                  required
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none text-xs sm:text-sm"
                  placeholder="Street address where courier can deliver physical certificate"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <label className="block text-[11px] sm:text-xs font-bold text-gray-700 uppercase mb-1">City *</label>
                  <input
                    type="text"
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none text-xs sm:text-sm"
                    placeholder="City"
                  />
                </div>

                <div>
                  <label className="block text-[11px] sm:text-xs font-bold text-gray-700 uppercase mb-1">State *</label>
                  <input
                    type="text"
                    required
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none text-xs sm:text-sm"
                    placeholder="State"
                  />
                </div>

                <div>
                  <label className="block text-[11px] sm:text-xs font-bold text-gray-700 uppercase mb-1">Pincode *</label>
                  <input
                    type="text"
                    required
                    value={formData.pincode}
                    onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none text-xs sm:text-sm"
                    placeholder="6-digit pincode"
                  />
                </div>
              </div>
            </div>

            {/* Submit & Pay Button */}
            <div className="pt-3 border-t border-gray-100">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold py-3 sm:py-3.5 rounded-xl transition-all flex items-center justify-center gap-2 text-sm sm:text-base shadow-md cursor-pointer disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" /> Launching Cashfree Gateway...
                  </>
                ) : (
                  <>
                    <CreditCard size={18} />
                    <span>Pay ₹{parseFloat(price || 1).toFixed(2)} & Submit Application</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default CertificateApplication;
